import React, { useMemo, useState } from 'react';
import { ServiceRequestDocument } from 'src/SharedCustomHooks/UseNotesAPI/types';
import Button from 'src/components/Button';
import FontAwesomeIcon from 'src/components/Icon';
import Empty from 'src/components/Empty';
import DocumentsRepeater from 'src/components/Documents/DocumentsRepeater/DocumentsRepeater';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import ModernizedDocumentForm from '../ModernizedDocumentForm/ModernizedDocumentForm';
import { filterDocumentsBySearchTerm } from 'src/components/DocumentsNewLocalState/helpers';
import { getUrlExtension } from 'src/helpers/CommonHelpers';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';
import { USER_ROLES } from 'src/common/constants';
import './ModernizedServiceRequestDocuments.scss';
import Search from 'src/components/Search/Search';

interface Props {
  documents: ServiceRequestDocument[];
  userId: string;
  saving: boolean;
  onAddDocument: (formData: DocumentFormData) => Promise<void>;
  onDeleteDocument?: (documentId: any) => Promise<void>;
  // TODO: derive role from JWT payload and pass in from useOfflineServiceRequestAPI
  user?: { id: string; roles: { role: { name: string } }[] };
  canDelete?: boolean;
}

const ModernizedServiceRequestDocuments: React.FC<Props> = ({
  documents,
  userId,
  saving,
  onAddDocument,
  onDeleteDocument,
  user,
  canDelete = false,
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const roleName: string = user?.roles?.[0]?.role?.name ?? '';

  const isHomeOwner: boolean =
    roleName === USER_ROLES.HOME_OWNER || roleName === USER_ROLES.OCCUPANT;

  const showAddButton: boolean = !(
    roleName === USER_ROLES.STRATA_MANAGER ||
    roleName === USER_ROLES.VENDOR ||
    roleName === USER_ROLES.OCCUPANT ||
    roleName === USER_ROLES.REALTOR ||
    (isHomeOwner && localStorage.getItem('theme') === 'Rize')
  );

  const displayDocuments = useMemo(
    () => filterDocumentsBySearchTerm(documents, searchTerm),
    [documents, searchTerm]
  );

  const handleClick = (_index: number, url: string, isImg: boolean): void => {
    if (isImg) {
      if (getUrlExtension(url) === 'heic') {
        window.open(url, '_blank')?.focus();
        return;
      }

      setSelectedImage(url);
      setLightboxOpen(true);
      return;
    }

    window.open(url, '_blank')?.focus();
  };

  const handleSave = async (formData: DocumentFormData): Promise<void> => {
    await onAddDocument(formData);
    setIsFormOpen(false);
  };

  const handleDelete = async (document: any): Promise<void> => {
    if (canDelete && onDeleteDocument) {
      await onDeleteDocument(document);
    }
  };

  return (
    <>
      <div className="offline-sr-docs-header">
        <div className="offline-sr-docs-search">
          <Search
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e: any) => setSearchTerm(e.target.value)}
            theme={undefined}
          />
        </div>
        {showAddButton && (
          <Button
            color="teal"
            label="Add Photos & Docs"
            onClick={() => setIsFormOpen(true)}
            icon={<FontAwesomeIcon glyph="plus" weight="light" size="small" />}
            size="small"
          />
        )}
      </div>
      {displayDocuments.length > 0 ? (
        <DocumentsRepeater
          documents={displayDocuments}
          onDeleteDocument={canDelete ? handleDelete : undefined}
          onDownload={(url: string) => window.open(url, '_blank')?.focus()}
          onClick={handleClick}
          user={user}
          from="Service Request"
          canDelete={canDelete}
        />
      ) : (
        <Empty
          headline="No photos or documents yet"
          strapline="Photos and documents attached to this service request will appear here."
          image={
            <img
              alt="No photos or documents yet"
              src="/assets/empty/documents.svg"
            />
          }
        />
      )}
      <ReusableLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        image={selectedImage}
      />
      <GenericDialogue
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title="Add Photos & Documents"
        description="Upload a photo or document to attach to this service request."
        maxWidth="135rem"
      >
        <ModernizedDocumentForm
          saving={saving}
          onSave={handleSave}
          onClose={() => setIsFormOpen(false)}
        />
      </GenericDialogue>
    </>
  );
};

export default ModernizedServiceRequestDocuments;
